<script setup>
import {computed, reactive, inject, ref, onMounted, watch, provide} from "vue";
//import functions
import {required} from "@vuelidate/validators";
import {discordRegistrationTime} from "@/utils/discordRegistrationTime.js";
import {formatPrice} from "@/utils/priceFormatter.js";
import {cleanObject} from "@/utils/cleanObject.js";
//import components
import {Icon} from "@iconify/vue";
import {
  Dialog,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  InputGroup,
  InputGroupAddon,
  InputText,
  FloatLabel,
  Select,
  Textarea,
  InputNumber,
  Rating,
  SelectButton
} from "primevue";
import Attachments from "@/views/orders/Details/Attachments.vue";
import Users from "@/components/Users.vue";
import UserProfile from "@/components/UserProfile/index.vue";
//import stores
import {useUserStore} from "@/stores/user.js";
import {useCurrencyPricesStore} from "@/stores/currencyPrices.js";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useVuelidate} from "@vuelidate/core";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {useRouter} from "vue-router";
import {showMessage} from "@/utils/showMessage.js";

//stores
const userStore = useUserStore();
const currencyPricesStore = useCurrencyPricesStore();
const ordersStore = useMainOrdersStore()

//loading flags
const {
  saving,
  fetching,
  setSaving,
  setFetching,
} = useLoadingFlags()
const isUploadingAttachment = ref(false)
const isShowUserProfileModal = ref(false);

//other composables
const {isXL} = useBreakpoint();
const toast = useToast();
const router = useRouter()

//emits
const emit = defineEmits(['save', 'refreshHotOrders']);

//props
const props = defineProps({
  orderType: {
    type: String,
    required: true
  },
  hotorder: {
    type: Object,
    required: true
  },
  onlyRating: {
    type: Boolean,
    default: false
  }
})

//injects
const visible = inject('isShowDeliveredRatingModal')

//component variables
const rating = ref(null)
const tags = ref([])
const form = reactive({
  "tags": props.hotorder.tags ? props.hotorder.tags : [],
  "rating": props.hotorder.rating ? Number(props.hotorder.rating) : null,
  "review": props.hotorder.review ? props.hotorder.review : null
})
const rules = computed(() => {
  return {
    rating: {required},
    review: form.rating < 4 ? {required} : {}
  };
});
const $vuelidate = useVuelidate(rules, form);


//component functions
const getTags = async () => {
  try {
    setFetching(true)
    const res = await ordersStore.action_getTags()
    if (res.result === true) {
      tags.value = res.data
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
      showMessage(res, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
    showMessage(err, 'error');
  } finally {
    setFetching(false)
  }
}

const saveRating = async () => {
  $vuelidate.value.$touch();
  if ($vuelidate.value.$invalid) {
    return false;
  }

  setSaving(true)
  const orderType = {
    Item: "Item",
    CurrencyBuy: "Currency",
    Boost: "Boost",
    'Gift Card': "Gift Card",
    Account: "Account",
  }
  const cleanRequest = {
    "id": props.hotorder.id,
    "type": orderType[props.orderType],
    "tags": form.tags,
    "rating": form.rating,
    "review": form.review,
    "userID": props.hotorder.takerID
  }
  const res = await ordersStore.action_setRatingAndTags(cleanRequest)
  if (res.result === true) {
    //toast.add({severity: 'success', summary: 'Successful', detail: `Rates and Tags applied!`, life: 1500});
    showMessage(`Rates and Tags applied!`, 'success');
    if (props.onlyRating) {
      emit('refreshHotOrders');
      //console.log('only rating')
    } else {
      emit('save');
    }
    visible.value = false
  } else {
    //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
    showMessage(res, 'error');
  }
  setSaving(false)
}

//hooks
onMounted(() => {
  getTags()
})

//provides
</script>
<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="min-w-full min-h-full flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        Positive Rating
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full">
      <div v-if="onlyRating" class="bg-slate-800 rounded-md p-3 border border-slate-700">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs max-w-2xl">
          <div>
            <p class="text-slate-400">Title</p>
            <p class="text-white w-full text-ellipsis whitespace-nowrap overflow-hidden">{{ hotorder.title }}</p>
          </div>
          <div>
            <p class="text-slate-400">Taker</p>
            <p class="text-white w-full text-ellipsis whitespace-nowrap overflow-hidden">{{ hotorder.takerName }}</p>
          </div>
          <div>
            <p class="text-slate-400">Rating Admin Name</p>
            <p class="text-white w-full text-ellipsis whitespace-nowrap overflow-hidden">{{ hotorder.ratingAdminName || '-' }}</p>
          </div>
        </div>
      </div>
      <div class="flex flex-col items-center gap-4">
        <Rating v-model="form.rating">
          <template #onicon>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 32 32">
              <path stroke-width="2" stroke="#fcd53f" fill="#fcd53f"
                    d="m18.7 4.627l2.247 4.31a2.27 2.27 0 0 0 1.686 1.189l4.746.65c2.538.35 3.522 3.479 1.645 5.219l-3.25 2.999a2.23 2.23 0 0 0-.683 2.04l.793 4.398c.441 2.45-2.108 4.36-4.345 3.24l-4.536-2.25a2.28 2.28 0 0 0-2.006 0l-4.536 2.25c-2.238 1.11-4.786-.79-4.345-3.24l.793-4.399c.14-.75-.12-1.52-.682-2.04l-3.251-2.998c-1.877-1.73-.893-4.87 1.645-5.22l4.746-.65a2.23 2.23 0 0 0 1.686-1.189l2.248-4.309c1.144-2.17 4.264-2.17 5.398 0"/>
            </svg>
          </template>
          <template #officon>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 32 32">
              <path stroke-width="2" stroke="#fcd53f" fill="transparent"
                    d="m18.7 4.627l2.247 4.31a2.27 2.27 0 0 0 1.686 1.189l4.746.65c2.538.35 3.522 3.479 1.645 5.219l-3.25 2.999a2.23 2.23 0 0 0-.683 2.04l.793 4.398c.441 2.45-2.108 4.36-4.345 3.24l-4.536-2.25a2.28 2.28 0 0 0-2.006 0l-4.536 2.25c-2.238 1.11-4.786-.79-4.345-3.24l.793-4.399c.14-.75-.12-1.52-.682-2.04l-3.251-2.998c-1.877-1.73-.893-4.87 1.645-5.22l4.746-.65a2.23 2.23 0 0 0 1.686-1.189l2.248-4.309c1.144-2.17 4.264-2.17 5.398 0"/>
            </svg>
          </template>
        </Rating>
        <template v-if="$vuelidate.rating.$error">
          <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
             v-if="!$vuelidate.rating.required.$response">
            You must rate this taker before continuing.
          </p>
        </template>
      </div>
      <div class="grid grid-cols-2 gap-6">
        <div class="flex flex-col gap-2">
          <p class="text-center select-none">Positive Points</p>
          <div v-if="fetching" class="grid grid-cols-1 md:grid-cols-2 gap-2 animate-pulse">
            <div v-for="i in 6" class="w-28 h-9 bg-slate-800 rounded-md"></div>
          </div>
          <SelectButton v-model="form.tags" :options="tags.filter(t => t.sign === '+')" optionLabel="tag"
                        optionValue="tag" multiple
                        aria-labelledby="multiple" class="!grid !grid-cols-1 md:!grid-cols-2 !gap-2 positive"/>
        </div>
        <div class="flex flex-col gap-2">
          <p class="text-center select-none">Negative Points</p>
          <div v-if="fetching" class="grid grid-cols-2 gap-2 animate-pulse">
            <div v-for="i in 6" class="w-28 h-9 bg-slate-800 rounded-md"></div>
          </div>
          <SelectButton v-model="form.tags" :options="tags.filter(t => t.sign === '-')" optionLabel="tag"
                        optionValue="tag" multiple
                        aria-labelledby="multiple" class="!grid !grid-cols-1 md:!grid-cols-2 !gap-2 negative"/>
        </div>
        <div class="col-span-2 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on" :class="{ 'has-error': $vuelidate.review.$error }">
            <Textarea v-auto-dir id="review" v-model="form.review" rows="5" style="resize: none"
                      :class="['!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700',
                           { 'has-error': $vuelidate.review.$error }
                         ]"/>
            <label for="review" class="!bg-slate-900">Review</label>
          </FloatLabel>
          <template v-if="$vuelidate.review.$error">
            <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
               v-if="!$vuelidate.review.required.$response">
              This field is required
            </p>
          </template>
        </div>
      </div>
    </div>
    <div class="flex justify-end gap-3">
      <button @click="saveRating"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1"
              :disabled="saving">
        Save
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
      </button>
    </div>
  </Dialog>
</template>
<style scoped>
:deep(.p-rating-option .p-rating-icon) {
  --p-rating-icon-size: 1.5rem
}
</style>